import { useState, useEffect, useRef, type FormEvent } from "react";
import { useAuth } from "../hooks/useAuth";
import { useCrypto } from "../hooks/useCrypto";
import { api } from "../lib/api";
import { subscribeToPush, unsubscribeFromPush, isPushSubscribed } from "../lib/notifications";
import { getCurrentTheme, setTheme, type Theme } from "../lib/theme";
import { ChevronLeft, Camera, Lock, Loader2, Volume2, Smartphone, Download, AlertTriangle, LogOut, ShieldCheck } from "lucide-react";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { Avatar } from "../components/Avatar";
import { AuthImage } from "../components/AuthImage";
import styles from "./SettingsPage.module.css";

export function SettingsPage({ onBack, onNavigateToAdmin }: { onBack: () => void; onNavigateToAdmin?: () => void }) {
  const { user } = useAuth();

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.backBtn} onClick={onBack}>
          <ChevronLeft size={18} /> Zurück
        </button>
        <h2 className={styles.title}>Einstellungen</h2>
      </div>

      <div className={styles.sections}>
        <ProfileSection />
        <ThemeSection />
        <VoiceSection />
        <VoiceRecognitionSection />
        <NotificationSection />

        {(user?.orgRole === "owner" || user?.orgRole === "admin") && <AdminPanelSection onNavigateToAdmin={onNavigateToAdmin} />}
        <PasswordSection />
        <TotpSection enabled={user?.totpEnabled ?? false} />
        <E2eeSection />
        <SessionSection />
        <DevicesSection />
        <DataPrivacySection />
      </div>
    </div>
  );
}

// ── Admin Panel Section ──
function AdminPanelSection({ onNavigateToAdmin }: { onNavigateToAdmin?: () => void }) {
  const handleClick = () => {
    if (onNavigateToAdmin) {
      onNavigateToAdmin();
    } else {
      window.history.pushState({}, "", "/admin");
      window.dispatchEvent(new PopStateEvent("popstate"));
    }
  };

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>
        <span style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <ShieldCheck size={18} /> Administration
        </span>
      </h3>
      <p className={styles.description}>
        Verwalte Benutzer, Bots und sieh dir das Audit-Log an.
      </p>
      <Button onClick={handleClick} size="sm" variant="secondary">
        <ShieldCheck size={14} /> Admin-Panel öffnen
      </Button>
    </section>
  );
}

// ── Theme Section ──
function ThemeSection() {
  const [current, setCurrent] = useState<Theme>(getCurrentTheme());

  const handleChange = (theme: Theme) => {
    setTheme(theme);
    setCurrent(theme);
  };

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>Design</h3>
      <div className={styles.themeOptions}>
        {(["system", "light", "dark"] as Theme[]).map((t) => (
          <button
            key={t}
            className={`${styles.themeBtn} ${current === t ? styles.themeBtnActive : ""}`}
            onClick={() => handleChange(t)}
          >
            {t === "system" ? "⚙️ System" : t === "light" ? "☀️ Hell" : "🌙 Dunkel"}
          </button>
        ))}
      </div>
    </section>
  );
}

// ── Voice Section (TTS) ──
function VoiceSection() {
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [selectedVoice, setSelectedVoice] = useState<string>(
    localStorage.getItem("tts-voice") ?? ""
  );
  const [testPlaying, setTestPlaying] = useState(false);

  // Load available voices
  useEffect(() => {
    if (!("speechSynthesis" in window)) return;

    const loadVoices = () => {
      const all = speechSynthesis.getVoices();
      const german = all.filter((v) => v.lang.startsWith("de"));
      setVoices(german);
      // If no voice selected yet, pick default
      if (!selectedVoice && german.length > 0) {
        const stored = localStorage.getItem("tts-voice");
        if (!stored) {
          setSelectedVoice(german[0]!.name);
        }
      }
    };

    loadVoices();
    speechSynthesis.addEventListener?.("voiceschanged", loadVoices);
    return () => speechSynthesis.removeEventListener?.("voiceschanged", loadVoices);
  }, []);

  const handleSelect = (voiceName: string) => {
    setSelectedVoice(voiceName);
    localStorage.setItem("tts-voice", voiceName);
  };

  const handleTest = () => {
    if (testPlaying) {
      speechSynthesis.cancel();
      setTestPlaying(false);
      return;
    }

    const voice = voices.find((v) => v.name === selectedVoice);
    if (!voice) return;

    const utterance = new SpeechSynthesisUtterance(
      "Hallo! So klinge ich. Gefällt dir meine Stimme?"
    );
    utterance.voice = voice;
    utterance.lang = voice.lang;
    utterance.rate = 1.0;
    utterance.pitch = 1.0;
    utterance.onstart = () => setTestPlaying(true);
    utterance.onend = () => setTestPlaying(false);
    utterance.onerror = () => setTestPlaying(false);
    speechSynthesis.speak(utterance);
  };

  if (!("speechSynthesis" in window)) {
    return null;
  }

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>Vorlesen (TTS)</h3>
      <p className={styles.description}>
        Wähle die Stimme zum Vorlesen von Nachrichten.
      </p>
      {voices.length === 0 ? (
        <p className={styles.description} style={{ opacity: 0.6 }}>
          Keine deutschen Stimmen verfügbar. Prüfe die Spracheinstellungen deines Geräts.
        </p>
      ) : (
        <>
          <div className={styles.voiceList}>
            {voices.map((voice) => (
              <button
                key={voice.name}
                className={`${styles.voiceItem} ${selectedVoice === voice.name ? styles.voiceItemActive : ""}`}
                onClick={() => handleSelect(voice.name)}
              >
                <Volume2 size={14} />
                <span className={styles.voiceName}>{voice.name}</span>
                <span className={styles.voiceLang}>{voice.lang}</span>
              </button>
            ))}
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={handleTest}
            disabled={!selectedVoice}
          >
            {testPlaying ? "⏹ Stoppen" : "▶ Probe hören"}
          </Button>
        </>
      )}
    </section>
  );
}

// ── Voice Recognition Mode Section ──
function VoiceRecognitionSection() {
  const [mode, setMode] = useState<"whisper" | "browser">(
    () => (localStorage.getItem("voice-recognition-mode") as "whisper" | "browser") || "whisper"
  );

  const handleChange = (newMode: "whisper" | "browser") => {
    setMode(newMode);
    localStorage.setItem("voice-recognition-mode", newMode);
  };

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>Spracherkennung (Voice Chat)</h3>
      <p className={styles.description}>
        Wähle wie deine Sprache im Voice-Chat erkannt wird.
      </p>
      <div className={styles.voiceList}>
        <button
          className={`${styles.voiceItem} ${mode === "whisper" ? styles.voiceItemActive : ""}`}
          onClick={() => handleChange("whisper")}
        >
          <span style={{ fontSize: 16 }}>🔒</span>
          <div style={{ textAlign: "left" }}>
            <span className={styles.voiceName}>Privat (Server)</span>
            <span className={styles.voiceLang}>Whisper AI auf deinem Server · ~2-4s · keine externen Dienste</span>
          </div>
        </button>
        <button
          className={`${styles.voiceItem} ${mode === "browser" ? styles.voiceItemActive : ""}`}
          onClick={() => handleChange("browser")}
        >
          <span style={{ fontSize: 16 }}>⚡</span>
          <div style={{ textAlign: "left" }}>
            <span className={styles.voiceName}>Schnell (Apple/Google)</span>
            <span className={styles.voiceLang}>Web Speech API · Echtzeit · Daten an Apple/Google</span>
          </div>
        </button>
      </div>
    </section>
  );
}

// ── Notification Section ──
function NotificationSection() {
  const [subscribed, setSubscribed] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    isPushSubscribed().then(setSubscribed).catch(() => setSubscribed(false));
  }, []);

  const handleToggle = async () => {
    setLoading(true);
    setError("");
    try {
      if (subscribed) {
        const success = await unsubscribeFromPush();
        if (success) setSubscribed(false);
        else setError("Benachrichtigungen konnten nicht deaktiviert werden");
      } else {
        const success = await subscribeToPush();
        if (success) setSubscribed(true);
        else setError("Benachrichtigungen konnten nicht aktiviert werden. Bitte erlaube Benachrichtigungen im Browser.");
      }
    } catch (err) {
      setError((err as { message?: string }).message ?? "Fehler");
    } finally {
      setLoading(false);
    }
  };

  const notSupported = !("serviceWorker" in navigator) || !("PushManager" in window);

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>Benachrichtigungen</h3>
      {notSupported ? (
        <p className={styles.description}>
          Push-Benachrichtigungen werden von diesem Browser nicht unterstützt.
        </p>
      ) : (
        <>
          <p className={styles.description}>
            Erhalte Benachrichtigungen über neue Nachrichten, auch wenn der Tab nicht aktiv ist.
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
            <Button
              onClick={handleToggle}
              loading={loading}
              size="sm"
              variant={subscribed ? "ghost" : "secondary"}
              disabled={subscribed === null}
            >
              {subscribed === null
                ? "Wird geprüft..."
                : subscribed
                  ? "🔔 Benachrichtigungen deaktivieren"
                  : "🔕 Benachrichtigungen aktivieren"}
            </Button>
            {subscribed && (
              <span className={styles.statusBadge}>
                <span className={styles.statusIcon}>🔔</span>
                <span>Aktiv</span>
              </span>
            )}
          </div>
          {error && <div className={styles.error}>{error}</div>}
        </>
      )}
    </section>
  );
}

// ── Profile Section ──
function ProfileSection() {
  const { user } = useAuth();
  const [displayName, setDisplayName] = useState(user?.displayName ?? "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [avatarUrl, setAvatarUrl] = useState(user?.avatarUrl ?? null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!displayName.trim()) return;

    setSaving(true);
    setError("");
    setSaved(false);

    try {
      await api.patch("/users/me", { displayName: displayName.trim() });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError((err as { message?: string }).message ?? "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setError("Bild darf max. 5 MB groß sein");
      return;
    }

    setUploadingAvatar(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("file", file);
      const data = await api.upload<{ user: { avatarUrl: string } }>("/users/me/avatar", formData);
      setAvatarUrl(data.user.avatarUrl);
      // Update auth store
      useAuth.setState((state) => ({
        user: state.user ? { ...state.user, avatarUrl: data.user.avatarUrl } : null,
      }));
    } catch (err) {
      setError((err as { message?: string }).message ?? "Fehler beim Upload");
    } finally {
      setUploadingAvatar(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleAvatarDelete = async () => {
    setUploadingAvatar(true);
    setError("");
    try {
      await api.delete("/users/me/avatar");
      setAvatarUrl(null);
      useAuth.setState((state) => ({
        user: state.user ? { ...state.user, avatarUrl: null } : null,
      }));
    } catch (err) {
      setError((err as { message?: string }).message ?? "Fehler beim Löschen");
    } finally {
      setUploadingAvatar(false);
    }
  };

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>Profil</h3>

      {/* Avatar Upload */}
      <div className={styles.avatarSection}>
        <div className={styles.avatarPreview} onClick={() => fileInputRef.current?.click()}>
          {avatarUrl ? (
            <AuthImage src={avatarUrl} alt="Avatar" className={styles.avatarImage} />
          ) : (
            <Avatar name={user?.displayName ?? "?"} size="xl" />
          )}
          <div className={styles.avatarOverlay}>
            {uploadingAvatar ? <Loader2 size={18} className="spin" /> : <Camera size={18} />}
          </div>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          onChange={handleAvatarUpload}
          style={{ display: "none" }}
        />
        <div className={styles.avatarActions}>
          <Button size="sm" variant="ghost" onClick={() => fileInputRef.current?.click()} loading={uploadingAvatar}>
            Bild ändern
          </Button>
          {avatarUrl && (
            <Button size="sm" variant="ghost" onClick={handleAvatarDelete} loading={uploadingAvatar}>
              Entfernen
            </Button>
          )}
        </div>
      </div>

      <form onSubmit={handleSave} className={styles.form}>
        <Input
          label="Anzeigename"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          required
        />
        {error && <div className={styles.error}>{error}</div>}
        <Button type="submit" loading={saving} size="sm">
          {saved ? "✓ Gespeichert" : "Speichern"}
        </Button>
      </form>

      <EmailChangeSection />
    </section>
  );
}

// ── Email Change Section ──
function EmailChangeSection() {
  const { user } = useAuth();
  const [step, setStep] = useState<"display" | "request" | "verify">("display");
  const [newEmail, setNewEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleRequestCode = async (e: FormEvent) => {
    e.preventDefault();
    if (!newEmail.trim() || !password) return;

    setSaving(true);
    setError("");

    try {
      await api.post("/users/me/email/request-change", {
        newEmail: newEmail.trim(),
        currentPassword: password,
      });
      setStep("verify");
      setPassword("");
    } catch (err) {
      setError((err as { message?: string }).message ?? "Fehler beim Senden");
    } finally {
      setSaving(false);
    }
  };

  const handleVerifyCode = async (e: FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return;

    setSaving(true);
    setError("");

    try {
      const data = await api.post<{ user: { email: string } }>("/users/me/email/confirm-change", {
        newEmail: newEmail.trim(),
        code: code.trim(),
      });
      useAuth.setState((state) => ({
        user: state.user ? { ...state.user, email: data.user.email } : null,
      }));
      setSuccess(true);
      setStep("display");
      setCode("");
      setNewEmail("");
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError((err as { message?: string }).message ?? "Code ungültig");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setStep("display");
    setError("");
    setPassword("");
    setCode("");
    setNewEmail("");
  };

  if (step === "display") {
    return (
      <div className={styles.form} style={{ marginTop: "1rem" }}>
        <div className={styles.field}>
          <label className={styles.label}>E-Mail</label>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <span>{success ? "✓ E-Mail geändert!" : user?.email}</span>
            <Button size="sm" variant="ghost" onClick={() => { setStep("request"); setNewEmail(user?.email ?? ""); }}>
              Ändern
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (step === "request") {
    return (
      <form onSubmit={handleRequestCode} className={styles.form} style={{ marginTop: "1rem" }}>
        <Input
          label="Neue E-Mail-Adresse"
          type="email"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          required
          autoFocus
        />
        <Input
          label="Aktuelles Passwort"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        {error && <div className={styles.error}>{error}</div>}
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <Button type="submit" loading={saving} size="sm">
            Bestätigungscode senden
          </Button>
          <Button size="sm" variant="ghost" onClick={handleCancel}>
            Abbrechen
          </Button>
        </div>
      </form>
    );
  }

  // step === "verify"
  return (
    <form onSubmit={handleVerifyCode} className={styles.form} style={{ marginTop: "1rem" }}>
      <p style={{ fontSize: "0.875rem", color: "var(--text-secondary)", margin: "0 0 0.75rem" }}>
        Ein 6-stelliger Code wurde an <strong>{newEmail}</strong> gesendet.
      </p>
      <Input
        label="Bestätigungscode"
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
        placeholder="123456"
        required
        autoFocus
        maxLength={6}
      />
      {error && <div className={styles.error}>{error}</div>}
      <div style={{ display: "flex", gap: "0.5rem" }}>
        <Button type="submit" loading={saving} size="sm" disabled={code.length !== 6}>
          E-Mail bestätigen
        </Button>
        <Button size="sm" variant="ghost" onClick={handleCancel}>
          Abbrechen
        </Button>
      </div>
    </form>
  );
}

// ── Password Section ──
function PasswordSection() {
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleChange = async (e: FormEvent) => {
    e.preventDefault();
    if (newPw !== confirmPw) {
      setError("Passwörter stimmen nicht überein");
      return;
    }
    if (newPw.length < 8) {
      setError("Neues Passwort muss mindestens 8 Zeichen haben");
      return;
    }

    setSaving(true);
    setError("");
    setSuccess(false);

    try {
      await api.post("/users/me/password", {
        currentPassword: currentPw,
        newPassword: newPw,
      });

      // Re-encrypt backup key with new password (fire-and-forget)
      try {
        const { reEncryptBackupKey } = await import("../lib/backup-key");
        const { useCrypto } = await import("../hooks/useCrypto");
        const bk = useCrypto.getState().backupKey;
        if (bk) {
          await reEncryptBackupKey(bk, newPw);
        }
      } catch {
        // Non-critical — backup key can be re-encrypted on next login
      }

      setSuccess(true);
      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError((err as { message?: string }).message ?? "Fehler beim Ändern");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>Passwort ändern</h3>
      <form onSubmit={handleChange} className={styles.form}>
        <Input
          label="Aktuelles Passwort"
          type="password"
          value={currentPw}
          onChange={(e) => setCurrentPw(e.target.value)}
          required
          autoComplete="current-password"
        />
        <Input
          label="Neues Passwort"
          type="password"
          value={newPw}
          onChange={(e) => setNewPw(e.target.value)}
          required
          autoComplete="new-password"
        />
        <Input
          label="Passwort bestätigen"
          type="password"
          value={confirmPw}
          onChange={(e) => setConfirmPw(e.target.value)}
          required
          autoComplete="new-password"
        />
        {error && <div className={styles.error}>{error}</div>}
        {success && <div className={styles.success}>✓ Passwort geändert</div>}
        <Button type="submit" loading={saving} size="sm">
          Passwort ändern
        </Button>
      </form>
    </section>
  );
}

// ── TOTP 2FA Section ──
function TotpSection({ enabled }: { enabled: boolean }) {
  const [step, setStep] = useState<"idle" | "setup" | "verify">("idle");
  const [qrCode, setQrCode] = useState("");
  const [secret, setSecret] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSetup = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await api.post<{ qrCode: string; secret: string; uri: string }>(
        "/auth/totp/setup",
      );
      setQrCode(data.qrCode);
      setSecret(data.secret);
      setStep("verify");
    } catch (err) {
      setError((err as { message?: string }).message ?? "Fehler beim Setup");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: FormEvent) => {
    e.preventDefault();
    if (totpCode.length !== 6) return;

    setLoading(true);
    setError("");
    try {
      await api.post("/auth/totp/verify", { totpCode });
      setSuccess(true);
      setStep("idle");
    } catch (err) {
      setError((err as { message?: string }).message ?? "Ungültiger Code");
    } finally {
      setLoading(false);
    }
  };

  if (enabled || success) {
    return (
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Zwei-Faktor-Authentifizierung</h3>
        <div className={styles.statusBadge}>
          <span className={styles.statusIcon}><Lock size={16} /></span>
          <span>2FA ist aktiviert</span>
        </div>
      </section>
    );
  }

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>Zwei-Faktor-Authentifizierung</h3>

      {step === "idle" && (
        <>
          <p className={styles.description}>
            Schütze dein Konto mit einer Authenticator-App (z.B. Google Authenticator, Authy).
          </p>
          <Button onClick={handleSetup} loading={loading} size="sm" variant="secondary">
            2FA einrichten
          </Button>
        </>
      )}

      {step === "verify" && (
        <div className={styles.totpSetup}>
          <p className={styles.description}>
            Scanne den QR-Code mit deiner Authenticator-App:
          </p>
          {qrCode && (
            <div className={styles.qrWrapper}>
              <img src={qrCode} alt="TOTP QR-Code" className={styles.qrCode} />
            </div>
          )}
          <details className={styles.secretDetails}>
            <summary>Manueller Code</summary>
            <code className={styles.secretCode}>{secret}</code>
          </details>

          <form onSubmit={handleVerify} className={styles.form}>
            <Input
              label="6-stelliger Code"
              type="text"
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="123456"
              maxLength={6}
              autoComplete="one-time-code"
            />
            {error && <div className={styles.error}>{error}</div>}
            <div className={styles.totpActions}>
              <Button onClick={() => setStep("idle")} variant="ghost" size="sm">
                Abbrechen
              </Button>
              <Button type="submit" loading={loading} size="sm" disabled={totpCode.length !== 6}>
                Bestätigen
              </Button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}

// ── Session Management Section ──
interface Session {
  id: string;
  deviceId: string | null;
  userAgent: string | null;
  ip: string | null;
  createdAt: string;
  expiresAt: string;
  isCurrent: boolean;
}

function SessionSection() {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(false);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [error, setError] = useState("");

  const loadSessions = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await api.get<{ sessions: Session[] }>("/auth/sessions");
      setSessions(data.sessions);
    } catch (err) {
      setError((err as { message?: string }).message ?? "Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSessions();
  }, []);

  const handleRevoke = async (id: string) => {
    setRevoking(id);
    try {
      await api.delete(`/auth/sessions/${id}`);
      setSessions((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      setError((err as { message?: string }).message ?? "Fehler");
    } finally {
      setRevoking(null);
    }
  };

  const handleRevokeAll = async () => {
    if (!confirm("Alle anderen Sitzungen beenden?")) return;
    setRevoking("all");
    try {
      await api.delete("/auth/sessions");
      setSessions((prev) => prev.filter((s) => s.isCurrent));
    } catch (err) {
      setError((err as { message?: string }).message ?? "Fehler");
    } finally {
      setRevoking(null);
    }
  };

  const parseUA = (ua: string | null): string => {
    if (!ua) return "Unbekanntes Gerät";
    if (ua.includes("Safari") && !ua.includes("Chrome")) return "Safari";
    if (ua.includes("Firefox")) return "Firefox";
    if (ua.includes("Chrome")) return "Chrome";
    if (ua.includes("Edge")) return "Edge";
    return "Browser";
  };

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>
        <span style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <Smartphone size={18} /> Aktive Sitzungen
        </span>
      </h3>
      <p className={styles.description}>
        Verwalte deine eingeloggten Geräte. Beende Sitzungen, die du nicht mehr brauchst.
      </p>

      {loading && <p className={styles.description} style={{ opacity: 0.6 }}>Wird geladen...</p>}
      {error && <div className={styles.error}>{error}</div>}

      {sessions.length > 0 && (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginBottom: "0.75rem" }}>
            {sessions.map((s) => (
              <div
                key={s.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "0.75rem",
                  borderRadius: "var(--radius-lg)",
                  border: `1px solid ${s.isCurrent ? "var(--color-accent)" : "var(--color-border-primary)"}`,
                  background: s.isCurrent ? "var(--color-accent-alpha-10, rgba(59,130,246,0.1))" : "transparent",
                  fontSize: "var(--text-sm)",
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <Smartphone size={14} />
                    <span style={{ fontWeight: 500 }}>
                      {parseUA(s.userAgent)}
                      {s.isCurrent && <span style={{ color: "var(--color-accent)", marginLeft: "0.5rem", fontSize: "var(--text-xs)" }}>Aktuelle Sitzung</span>}
                    </span>
                  </div>
                  <div style={{ color: "var(--color-text-muted)", fontSize: "var(--text-xs)", marginTop: "0.25rem" }}>
                    {s.ip ?? "—"} · Seit {new Date(s.createdAt).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
                {!s.isCurrent && (
                  <button
                    onClick={() => handleRevoke(s.id)}
                    disabled={revoking === s.id}
                    title="Sitzung beenden"
                    style={{
                      padding: "0.4rem",
                      borderRadius: "var(--radius-md)",
                      color: "var(--color-error)",
                      background: "transparent",
                      cursor: "pointer",
                      border: "none",
                      opacity: revoking === s.id ? 0.5 : 1,
                    }}
                  >
                    <LogOut size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
          {sessions.filter((s) => !s.isCurrent).length > 0 && (
            <Button
              onClick={handleRevokeAll}
              loading={revoking === "all"}
              size="sm"
              variant="ghost"
              style={{ color: "var(--color-error)" }}
            >
              <LogOut size={14} /> Überall abmelden
            </Button>
          )}
        </>
      )}
    </section>
  );
}

// ── E2EE / Encryption Section ──
function E2eeSection() {
  const { initialized, deviceId, backupKey } = useCrypto();

  if (!initialized) return null;

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>
        <Lock size={16} style={{ display: "inline", verticalAlign: "-2px", marginRight: "6px" }} />
        Ende-zu-Ende-Verschlüsselung
      </h3>

      <div className={styles.description}>
        Alle Nachrichten sind Ende-zu-Ende verschlüsselt. Nur du und dein Gesprächspartner können sie lesen.
      </div>

      {/* Device Info */}
      <div className={styles.field}>
        <span className={styles.label}>Geräte-ID</span>
        <span className={styles.readOnly} style={{ fontFamily: "var(--font-mono)", fontSize: "var(--text-xs)" }}>
          {deviceId}
        </span>
      </div>

      {/* Backup Status */}
      <div className={styles.field} style={{ marginTop: "var(--space-3)" }}>
        <span className={styles.label}>Nachrichten-Backup</span>
        <span className={styles.readOnly}>
          {backupKey ? "✅ Aktiv (automatisch via Passwort)" : "⏳ Wird beim nächsten Login aktiviert"}
        </span>
      </div>

      <p className={styles.description} style={{ marginTop: "var(--space-2)", opacity: 0.7 }}>
        Nachrichten werden automatisch mit deinem Login-Passwort verschlüsselt gesichert.
        Beim Anmelden auf einem neuen Gerät werden sie automatisch wiederhergestellt.
      </p>
    </section>
  );
}

// ── Data Privacy / DSGVO Section ──
function DataPrivacySection() {
  const { logout } = useAuth();
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [error, setError] = useState("");

  const handleExport = async () => {
    setExporting(true);
    setError("");
    try {
      const data = await api.get<Record<string, unknown>>("/users/me/data-export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `kollegeplus-daten-${new Date().toISOString().split("T")[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError((err as { message?: string }).message ?? "Fehler beim Export");
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async (e: FormEvent) => {
    e.preventDefault();
    if (!deletePassword) return;

    setDeleting(true);
    setError("");
    try {
      await api.delete("/users/me", { body: JSON.stringify({ password: deletePassword }) });
      logout();
    } catch (err) {
      setError((err as { message?: string }).message ?? "Fehler beim Löschen");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>Datenschutz</h3>

      {/* Data Export */}
      <p className={styles.description}>
        Lade alle deine Daten herunter (DSGVO Art. 20 — Datenportabilität).
      </p>
      <Button onClick={handleExport} loading={exporting} size="sm" variant="secondary" style={{ marginBottom: "1rem" }}>
        <Download size={14} /> Meine Daten exportieren
      </Button>

      {/* Account Deletion */}
      <div style={{ borderTop: "1px solid var(--color-border-primary)", paddingTop: "1rem" }}>
        {!showDelete ? (
          <Button onClick={() => setShowDelete(true)} size="sm" variant="ghost" style={{ color: "var(--color-error)" }}>
            <AlertTriangle size={14} /> Konto löschen
          </Button>
        ) : (
          <div>
            <p className={styles.description} style={{ color: "var(--color-error)" }}>
              ⚠️ Dein Konto wird unwiderruflich gelöscht. Deine Nachrichten bleiben anonymisiert erhalten.
            </p>
            <form onSubmit={handleDelete} className={styles.form}>
              <Input
                label="Passwort bestätigen"
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                required
                autoComplete="current-password"
              />
              {error && <div className={styles.error}>{error}</div>}
              <div style={{ display: "flex", gap: "0.5rem" }}>
                <Button onClick={() => { setShowDelete(false); setDeletePassword(""); setError(""); }} variant="ghost" size="sm">
                  Abbrechen
                </Button>
                <Button type="submit" loading={deleting} size="sm" style={{ background: "var(--color-error)" }}>
                  <AlertTriangle size={14} /> Endgültig löschen
                </Button>
              </div>
            </form>
          </div>
        )}
      </div>
    </section>
  );
}

// ── Devices Section (F1b) ──
interface DeviceEntry {
  id: string;
  deviceId: string;
  displayName: string | null;
  isActive: boolean;
  lastActiveAt: string;
  createdAt: string;
  prekeyCount: number;
}

function formatRelative(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 2) return "gerade eben";
  if (mins < 60) return `vor ${mins} Min.`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `vor ${hrs} Std.`;
  const days = Math.floor(hrs / 24);
  return `vor ${days} Tag${days === 1 ? "" : "en"}`;
}

function DevicesSection() {
  const [devList, setDevList] = useState<DeviceEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [deactivating, setDeactivating] = useState<string | null>(null);
  const [error, setError] = useState("");
  const currentDeviceId = localStorage.getItem("kollegeplus:deviceId") ?? "";

  const loadDevices = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await api.get<{ devices: DeviceEntry[] }>("/users/me/devices");
      setDevList(data.devices);
    } catch (err) {
      setError((err as { message?: string }).message ?? "Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadDevices(); }, []);

  const handleDeactivate = async (deviceId: string) => {
    if (!confirm("Dieses E2EE-Gerät deaktivieren? Es empfängt dann keine neuen verschlüsselten Nachrichten mehr.")) return;
    setDeactivating(deviceId);
    try {
      await api.delete(`/users/me/devices/${deviceId}`);
      setDevList((prev) => prev.map((d) => d.deviceId === deviceId ? { ...d, isActive: false } : d));
    } catch (err) {
      setError((err as { message?: string }).message ?? "Fehler");
    } finally {
      setDeactivating(null);
    }
  };

  const activeCount = devList.filter((d) => d.isActive).length;

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>
        <span style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <ShieldCheck size={18} /> E2EE-Geräte
        </span>
      </h3>
      <p className={styles.description}>
        Jedes Gerät hat eigene Verschlüsselungsschlüssel. Inaktive Geräte blockieren keine Nachrichten mehr — deaktiviere alte Geräte um Phantom-Keys zu vermeiden.
      </p>

      {activeCount > 10 && (
        <div style={{ padding: "0.75rem", borderRadius: "var(--radius-lg)", background: "var(--color-warning-alpha-10, rgba(234,179,8,0.12))", border: "1px solid var(--color-warning, #ca8a04)", marginBottom: "0.75rem", fontSize: "var(--text-sm)" }}>
          ⚠️ Du hast <strong>{activeCount} aktive E2EE-Geräte</strong>. Mehr als 10 können Verschlüsselungsprobleme verursachen. Bitte deaktiviere nicht mehr genutzte Geräte.
        </div>
      )}

      {loading && <p className={styles.description} style={{ opacity: 0.6 }}>Wird geladen...</p>}
      {error && <div className={styles.error}>{error}</div>}

      {devList.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          {devList.map((d) => {
            const isCurrent = d.deviceId === currentDeviceId;
            return (
              <div
                key={d.deviceId}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "0.75rem",
                  borderRadius: "var(--radius-lg)",
                  border: `1px solid ${isCurrent ? "var(--color-accent)" : d.isActive ? "var(--color-border-primary)" : "var(--color-border-muted, var(--color-border-primary))"}`,
                  background: isCurrent ? "var(--color-accent-alpha-10, rgba(59,130,246,0.1))" : "transparent",
                  opacity: d.isActive ? 1 : 0.5,
                  fontSize: "var(--text-sm)",
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontWeight: 500 }}>
                    <Smartphone size={14} />
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {d.displayName ?? d.deviceId}
                    </span>
                    {isCurrent && <span style={{ color: "var(--color-accent)", fontSize: "var(--text-xs)", flexShrink: 0 }}>Dieses Gerät</span>}
                    {!d.isActive && <span style={{ color: "var(--color-text-muted)", fontSize: "var(--text-xs)", flexShrink: 0 }}>Inaktiv</span>}
                  </div>
                  <div style={{ color: "var(--color-text-muted)", fontSize: "var(--text-xs)", marginTop: "0.25rem" }}>
                    Zuletzt aktiv: {formatRelative(d.lastActiveAt)} · {d.prekeyCount} Pre-Keys · ID: …{d.deviceId.slice(-6)}
                  </div>
                </div>
                {!isCurrent && d.isActive && (
                  <button
                    onClick={() => handleDeactivate(d.deviceId)}
                    disabled={deactivating === d.deviceId}
                    title="Gerät deaktivieren"
                    style={{
                      padding: "0.4rem",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "var(--color-text-muted)",
                      flexShrink: 0,
                      opacity: deactivating === d.deviceId ? 0.5 : 1,
                    }}
                  >
                    {deactivating === d.deviceId ? <Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} /> : <LogOut size={16} />}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
